import { analyticsService } from './analyticsService';
import { bookingService } from './bookingService';
import { vehicleService } from './vehicleService';
import { Booking } from '../types/booking';
import { Vehicle } from '../types/vehicle';

export interface VehiclePerformanceData {
  vehicleId: string;
  make: string;
  model: string;
  year: number;
  totalBookings: number;
  totalRevenue: number;
  utilizationRate: number;
  averageRating?: number;
  status: string;
}

class VehiclePerformanceService {
  async getVehiclePerformance(): Promise<VehiclePerformanceData[]> {
    try {
      const topPerformers = await analyticsService.getTopPerformingVehicles(20);
      if (topPerformers.success && topPerformers.data && topPerformers.data.length > 0) {
        return topPerformers.data.map((item: any) => ({
          vehicleId: item.vehicleId || '',
          make: item.vehicleBrand || item.make || '',
          model: item.vehicleModel || item.model || item.vehicleName || 'Unknown',
          year: item.year || 0,
          totalBookings: item.totalBookings || 0,
          totalRevenue: item.totalRevenue || 0,
          utilizationRate: item.averageUtilization || 0,
          status: item.status || 'available',
        }));
      }
    } catch (error) {
      console.error('Top performers endpoint unavailable, using fallback:', error);
    }

    return this.getVehiclePerformanceFallback();
  }

  private async getVehiclePerformanceFallback(): Promise<VehiclePerformanceData[]> {
    try {
      const now = new Date();
      const startDate = new Date(now);
      startDate.setDate(now.getDate() - 90);

      const [bookingsResponse, vehiclesResponse] = await Promise.all([
        bookingService.getAllBookings({ startDate, endDate: now }),
        vehicleService.getAllVehicles(),
      ]);

      if (!bookingsResponse.success || !vehiclesResponse.success) {
        return [];
      }

      const bookings = bookingsResponse.data || [];
      const vehicles = vehiclesResponse.data || [];

      const performanceMap = new Map<string, VehiclePerformanceData>();

      vehicles.forEach((vehicle: Vehicle) => {
        performanceMap.set(vehicle._id, {
          vehicleId: vehicle._id,
          make: vehicle.brand,
          model: vehicle.model,
          year: vehicle.year,
          totalBookings: 0,
          totalRevenue: 0,
          utilizationRate: 0,
          status: vehicle.status,
        });
      });

      bookings.forEach((booking: Booking) => {
        if (booking.car && booking.car._id) {
          const carId = typeof booking.car === 'string' ? booking.car : booking.car._id;
          const performance = performanceMap.get(carId);

          if (performance) {
            performance.totalBookings++;
            performance.totalRevenue += booking.totalAmount || 0;
          }
        }
      });

      const totalBookings = bookings.length;
      performanceMap.forEach((performance) => {
        if (totalBookings > 0) {
          performance.utilizationRate = (performance.totalBookings / totalBookings) * 100;
        }
      });

      return Array.from(performanceMap.values()).sort(
        (a, b) => b.utilizationRate - a.utilizationRate,
      );
    } catch (error) {
      console.error('Error fetching vehicle performance:', error);
      return [];
    }
  }

  getUtilizationColor(rate: number): string {
    if (rate >= 80) return 'bg-green-500';
    if (rate >= 60) return 'bg-blue-500';
    if (rate >= 40) return 'bg-yellow-500';
    if (rate >= 20) return 'bg-orange-500';
    return 'bg-red-500';
  }

  getUtilizationLabel(rate: number): string {
    if (rate >= 80) return 'Excellent';
    if (rate >= 60) return 'Good';
    if (rate >= 40) return 'Average';
    if (rate >= 20) return 'Low';
    return 'Very Low';
  }
}

export const vehiclePerformanceService = new VehiclePerformanceService();
